import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, ArrowRight, ArrowLeft, CheckCircle, Send, User, Phone, Tag, Copy, Package, Plus, Minus, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatEventDateTime } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DemoEvent() {
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedTier, setSelectedTier] = useState(null);
  const [entryQuantity, setEntryQuantity] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ guest_name: '', guest_email: '', payment_method_used: 'Zelle' });

  useEffect(() => {
    axios.get(`${API}/demo/event`).then(res => { setEvent(res.data); setLoading(false); }).catch(() => { toast.error('Failed to load demo'); setLoading(false); });
  }, []);

  if (loading || !event) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#C6A66B] border-t-transparent rounded-full animate-spin" /></div>;

  const entryTiers = event.pass_tiers.filter(t => t.type === 'entry');
  const addonTiers = event.pass_tiers.filter(t => t.type === 'addon' && t.status_default === 'active');
  const isComp = selectedTier?.is_comp;

  const getTotal = () => {
    if (!selectedTier) return 0;
    if (isComp) return 0;
    let total = selectedTier.price * entryQuantity;
    Object.entries(selectedAddOns).forEach(([id, qty]) => {
      const ao = addonTiers.find(a => a.id === id);
      if (ao) total += ao.price * qty;
    });
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.guest_name || !form.guest_email) { toast.error('Please fill in all fields'); return; }
    setSubmitting(true);
    try {
      const addOns = Object.entries(selectedAddOns).map(([id, qty]) => {
        const ao = addonTiers.find(a => a.id === id);
        return { name: ao.tier_name, price: ao.price, quantity: qty };
      });
      const res = await axios.post(`${API}/demo/ticket`, {
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        pass_tier_name: selectedTier.tier_name,
        entry_quantity: entryQuantity,
        attendees_count: entryQuantity * (selectedTier.attendees_per_pass || 1),
        amount_paid: getTotal(),
        purchased_add_ons: addOns,
        payment_method_used: isComp ? 'Complimentary' : 'Zelle',
        is_comp: isComp || false,
      });
      navigate(`/ticket/${res.data.ticket_id}?demo=true`);
    } catch { toast.error('Something went wrong'); } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Demo banner */}
      <div className="bg-[#C6A66B]/10 border-b border-[#C6A66B]/20 py-2 px-4 flex items-center justify-center gap-3" data-testid="demo-banner">
        <span className="text-xs font-semibold text-[#C6A66B] tracking-wider uppercase">Guest Experience Demo</span>
        <button onClick={() => navigate('/')} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-[#C6A66B]/30 text-[#C6A66B] hover:bg-[#C6A66B]/10 transition-colors" data-testid="exit-demo-button">
          Exit Demo
        </button>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Event header */}
        <div className="text-center mb-8" data-testid="demo-event-header">
          <div className="text-[10px] tracking-[0.3em] text-[#C6A66B] font-semibold uppercase mb-3">VelvetGate Event</div>
          <h1 className="text-2xl sm:text-3xl font-black text-[#F3EEE7] tracking-tight mb-2">{event.title}</h1>
          <p className="text-sm text-[#B9B1A6] italic mb-4">{event.tagline}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-[#B9B1A6]">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#C6A66B]" />{formatEventDateTime(event.date, event.time)}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#C6A66B]" />{event.location}</span>
          </div>
          <p className="text-xs text-[#777] mt-3 max-w-sm mx-auto">{event.description}</p>
        </div>

        {/* Step 1: Select tier */}
        {step === 1 && (
          <div className="space-y-3" data-testid="tier-selection">
            <div className="text-xs text-[#777] uppercase tracking-widest font-semibold mb-3">Select Your Pass</div>
            {entryTiers.map(tier => (
              <button key={tier.id} type="button" onClick={() => { setSelectedTier(tier); setEntryQuantity(1); }} className={`w-full text-left p-4 rounded-sm border-2 transition-all ${selectedTier?.id === tier.id ? 'border-[#C6A66B] bg-[#C6A66B]/5' : 'border-[#1A1A1A] hover:border-[#333]'}`} data-testid={`tier-${tier.id}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-[#F3EEE7] text-sm">{tier.tier_name}</div>
                    {tier.is_comp && <div className="text-[10px] text-[#C6A66B] font-bold mt-0.5">COMPLIMENTARY</div>}
                  </div>
                  <div className="text-lg font-bold text-[#C6A66B]">{tier.is_comp ? 'FREE' : `$${tier.price}`}</div>
                </div>
              </button>
            ))}

            {selectedTier && !isComp && addonTiers.length > 0 && (
              <div className="mt-6" data-testid="addon-selection">
                <div className="text-xs text-[#777] uppercase tracking-widest font-semibold mb-3">Add Extras</div>
                {addonTiers.map(ao => {
                  const qty = selectedAddOns[ao.id] || 0;
                  return (
                    <div key={ao.id} className="flex items-center justify-between p-3 border border-[#1A1A1A] rounded-sm mb-2" data-testid={`addon-${ao.id}`}>
                      <div>
                        <div className="text-sm font-medium text-[#F3EEE7]">{ao.tier_name}</div>
                        <div className="text-xs text-[#C6A66B]">${ao.price}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {qty > 0 && <button type="button" onClick={() => setSelectedAddOns(p => { const n = { ...p }; if (n[ao.id] <= 1) delete n[ao.id]; else n[ao.id]--; return n; })} className="w-7 h-7 rounded-sm bg-[#1A1A1A] flex items-center justify-center text-[#AFAFAF] hover:text-[#F3EEE7]"><Minus className="w-3 h-3" /></button>}
                        {qty > 0 && <span className="text-sm font-bold text-[#F3EEE7] w-5 text-center">{qty}</span>}
                        <button type="button" onClick={() => setSelectedAddOns(p => ({ ...p, [ao.id]: (p[ao.id] || 0) + 1 }))} className="w-7 h-7 rounded-sm bg-[#1A1A1A] flex items-center justify-center text-[#AFAFAF] hover:text-[#F3EEE7]"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedTier && (
              <div className="mt-4 pt-4 border-t border-[#1A1A1A]">
                <div className="flex justify-between items-baseline mb-4">
                  <span className="text-sm text-[#B9B1A6]">Total</span>
                  <span className="text-2xl font-black text-[#C6A66B]">{isComp ? 'FREE' : `$${getTotal().toFixed(2)}`}</span>
                </div>
                <Button onClick={() => setStep(2)} className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] rounded-sm py-3.5 font-bold" data-testid="continue-to-payment">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
          <div data-testid="payment-step">
            {/* Summary */}
            <div className="mb-5 border border-[#C6A66B]/30 rounded-sm overflow-hidden" data-testid="demo-payment-summary">
              <div className="bg-[#0D0D0D] px-4 py-3 border-b border-[#C6A66B]/15">
                <div className="text-[10px] text-[#C6A66B] uppercase tracking-widest font-semibold">Order Summary</div>
              </div>
              <div className="px-4 py-3 space-y-2 bg-[#050505]">
                <div className="flex justify-between text-sm"><span className="text-[#B9B1A6]">{selectedTier.tier_name} x{entryQuantity}</span><span className="text-[#F3EEE7]">{isComp ? 'FREE' : `$${(selectedTier.price * entryQuantity).toFixed(2)}`}</span></div>
                {Object.entries(selectedAddOns).map(([id, qty]) => { const ao = addonTiers.find(a => a.id === id); if (!ao) return null; return <div key={id} className="flex justify-between text-sm"><span className="text-[#B9B1A6]">{ao.tier_name} x{qty}</span><span className="text-[#F3EEE7]">${(ao.price * qty).toFixed(2)}</span></div>; })}
                <div className="flex justify-between items-baseline pt-2 border-t border-[#1A1A1A]"><span className="text-sm font-semibold text-[#F3EEE7]">Total</span><span className="text-2xl font-black text-[#C6A66B]">{isComp ? 'FREE' : `$${getTotal().toFixed(2)}`}</span></div>
              </div>
            </div>

            {!isComp && (
              <>
                {/* Instructions */}
                <div className="mb-5 p-4 border border-[#1A1A1A] rounded-sm bg-[#0A0A0A]" data-testid="demo-payment-instructions">
                  <ol className="space-y-2">
                    {[
                      'Open your banking or payment app',
                      `Send $${getTotal().toFixed(2)} to the recipient below`,
                      'Include the suggested memo',
                      'Return and confirm'
                    ].map((s, i) => (
                      <li key={`step-${i + 1}`} className="flex items-start gap-3 text-sm">
                        <span className="w-5 h-5 rounded-full bg-[#C6A66B]/10 text-[#C6A66B] flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">{i + 1}</span>
                        <span className="text-[#B9B1A6]">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Zelle card */}
                <div className="mb-5 border-2 border-[#1A1A1A] rounded-sm overflow-hidden" data-testid="demo-zelle-card">
                  <div className="bg-[#111] px-5 py-3 flex items-center gap-2 border-b border-[#1A1A1A]">
                    <Send className="w-4 h-4 text-[#C6A66B]" />
                    <span className="font-semibold text-[#F3EEE7]">Pay via Zelle</span>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded bg-[#1a2a1a] text-[#6EE7B7] font-medium">Direct Transfer</span>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    <div className="flex items-center gap-3"><div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><User className="w-3.5 h-3.5 text-[#AFAFAF]" /></div><div><div className="text-xs text-[#777] uppercase tracking-wider">Send to</div><div className="text-sm font-semibold text-[#F3EEE7]">VelvetGate Demo</div></div></div>
                    <div className="flex items-center gap-3"><div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><Phone className="w-3.5 h-3.5 text-[#AFAFAF]" /></div><div><div className="text-xs text-[#777] uppercase tracking-wider">Email</div><div className="text-sm font-semibold text-[#F3EEE7] font-mono">demo@velvetgate.com</div></div></div>
                    <div className="flex items-center gap-3"><div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0"><Tag className="w-3.5 h-3.5 text-[#AFAFAF]" /></div><div><div className="text-xs text-[#777] uppercase tracking-wider">Memo</div><div className="text-sm font-semibold text-[#C6A66B] italic">"VG-DEMO"</div></div></div>
                  </div>
                </div>

                {/* Demo note */}
                <div className="mb-5 flex items-start gap-2 p-3 bg-[#C6A66B]/5 border border-[#C6A66B]/15 rounded-sm" data-testid="demo-note">
                  <Info className="w-4 h-4 text-[#C6A66B] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#C6A66B]">Demo Mode — no payment required. Continue to experience the full flow.</p>
                </div>
              </>
            )}

            {isComp && (
              <div className="mb-5 text-center py-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a2a1a] rounded-sm">
                  <CheckCircle className="w-5 h-5 text-[#6EE7B7]" />
                  <span className="text-[#6EE7B7] font-semibold text-sm">No payment required — Complimentary</span>
                </div>
              </div>
            )}

            <Button onClick={() => setStep(3)} className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] rounded-sm py-3.5 font-bold text-sm" data-testid="demo-sent-payment">
              <CheckCircle className="w-4 h-4 mr-2" />I've Sent Payment<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button type="button" onClick={() => setStep(1)} variant="ghost" className="mt-2 w-full text-[#777] hover:text-[#F3EEE7] text-sm"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          </div>
        )}

        {/* Step 3: Confirmation form */}
        {step === 3 && (
          <div data-testid="confirmation-step">
            <h3 className="text-lg font-bold text-[#F3EEE7] mb-4">Get Your Demo Pass</h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
              <div>
                <Label className="text-[#F3EEE7]">Full Name *</Label>
                <Input value={form.guest_name} onChange={e => setForm(p => ({ ...p, guest_name: e.target.value }))} required className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7] focus:border-[#C6A66B]" data-testid="demo-name-input" />
              </div>
              <div>
                <Label className="text-[#F3EEE7]">Email *</Label>
                <Input type="email" value={form.guest_email} onChange={e => setForm(p => ({ ...p, guest_email: e.target.value }))} required className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7] focus:border-[#C6A66B]" data-testid="demo-email-input" />
              </div>
              <div className="flex gap-3">
                <Button type="button" onClick={() => setStep(2)} variant="outline" className="flex-1 border-[#1A1A1A] text-[#777] hover:text-[#F3EEE7]">Back</Button>
                <Button type="submit" className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] font-bold" disabled={submitting} data-testid="demo-get-pass">
                  {submitting ? 'Processing...' : 'Get My Demo Pass'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
